import React, { useState, useEffect } from 'react';
import { CourseEnrollmentService } from '../../firebase/unifiedDataService';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { Logger } from '../../utils/logger';
import { 
  createCourseInvite, 
  getCourseInvites, 
  deactivateInvite, 
  deleteInvite,
  getInviteLink,
  copyInviteLinkToClipboard 
} from '../../utils/courseInviteService';
import { notifyError, notifyConfirm } from '../../services/notificationService';

/**
 * CourseParticipantsTab - Shows enrolled students and invitation management
 */
export default function CourseParticipantsTab({ course, courseId }) {
  const { user } = useUnifiedAuth();
  
  const [enrollments, setEnrollments] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  
  // Create invite state
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [inviteLabel, setInviteLabel] = useState('');
  const [inviteExpiry, setInviteExpiry] = useState('3'); // Default 72h
  const [inviteLimit, setInviteLimit] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Copy success feedback
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Load enrollments using CourseEnrollmentService
  useEffect(() => {
    if (!user || !courseId) return;
    
    const unsubscribe = CourseEnrollmentService.subscribeEnrollments(user.uid, courseId, (enrollmentList) => {
      const list = enrollmentList.map(enrollment => ({
        ...enrollment,
        studentId: enrollment.studentId
      }));
      setEnrollments(list.sort((a, b) => (b.enrolledAt || 0) - (a.enrolledAt || 0)));
      setLoadingEnrollments(false);
    });
    
    return () => unsubscribe && unsubscribe();
  }, [user, courseId]);
  
  // Load invites
  useEffect(() => {
    const loadInvites = async () => {
      if (!user || !courseId) return;
      
      try {
        const courseInvites = await getCourseInvites(user.uid, courseId);
        setInvites(courseInvites);
      } catch (err) {
        log.error('Error loading invites', err);
      } finally {
        setLoadingInvites(false);
      }
    };
    
    loadInvites();
  }, [user, courseId]);
  
  // Create new invite
  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!user || isCreating) return;
    
    setIsCreating(true);
    try {
      const newInvite = await createCourseInvite({
        teacherUserId: user.uid,
        courseId,
        courseName: course?.name || 'Kurs',
        label: inviteLabel.trim() || undefined,
        expiresInDays: inviteExpiry ? parseInt(inviteExpiry) : null,
        usageLimit: inviteLimit ? parseInt(inviteLimit) : 0
      });
      
      setInvites([newInvite, ...invites]);
      setShowCreateInvite(false);
      setInviteLabel('');
      setInviteExpiry('');
      setInviteLimit('');
      
    } catch (err) {
      log.error('Error creating invite', err);
      notifyError('Fehler beim Erstellen des Einladungscodes: ' + err.message);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Copy invite link
  const handleCopyLink = async (code) => {
    try {
      await copyInviteLinkToClipboard(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      log.error('Error copying link', err);
    }
  };
  
  // Deactivate invite
  const handleDeactivateInvite = async (code) => {
    const confirmed = await notifyConfirm(
      'Einladungscode wirklich deaktivieren?',
      { title: 'Deaktivieren bestätigen', variant: 'warning' }
    );
    if (!confirmed) return;
    
    try {
      await deactivateInvite(code, user.uid);
      setInvites(invites.map(i => 
        i.code === code ? { ...i, isActive: false } : i
      ));
    } catch (err) {
      notifyError('Fehler: ' + err.message);
    }
  };
  
  // Delete invite
  const handleDeleteInvite = async (code) => {
    const confirmed = await notifyConfirm(
      'Einladungscode wirklich löschen?',
      { title: 'Löschen bestätigen', variant: 'danger' }
    );
    if (!confirmed) return;
    
    try {
      await deleteInvite(code, user.uid);
      setInvites(invites.filter(i => i.code !== code));
    } catch (err) {
      notifyError('Fehler: ' + err.message);
    }
  };
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Invitations Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Einladungslinks
          </h2>
          <button
            onClick={() => setShowCreateInvite(!showCreateInvite)}
            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg theme-transition"
          >
            {showCreateInvite ? 'Abbrechen' : '+ Neuer Link'}
          </button>
        </div>
        
        {/* Create Invite Form */}
        {showCreateInvite && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleCreateInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bezeichnung (optional)
                </label>
                <input
                  type="text"
                  value={inviteLabel}
                  onChange={(e) => setInviteLabel(e.target.value)}
                  placeholder="z.B. WiSe 2024/25"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gültig für (Tage)
                  </label>
                  <select
                    value={inviteExpiry}
                    onChange={(e) => setInviteExpiry(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="3">3 Tage (72h)</option>
                    <option value="7">7 Tage</option>
                    <option value="30">30 Tage</option>
                    <option value="90">90 Tage</option>
                    <option value="">Unbegrenzt</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max. Nutzungen
                  </label>
                  <select
                    value={inviteLimit}
                    onChange={(e) => setInviteLimit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Unbegrenzt</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </select>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isCreating}
                className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg theme-transition disabled:opacity-50"
              >
                {isCreating ? 'Erstelle...' : 'Einladungslink erstellen'}
              </button>
            </form>
          </div>
        )}
        
        {/* Invites List */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loadingInvites ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Lade Einladungen...
            </div>
          ) : invites.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-2">📨</div>
              <p className="text-gray-600 dark:text-gray-400">
                Noch keine Einladungslinks erstellt.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                Erstelle einen Link, um Studierende einzuladen.
              </p>
            </div>
          ) : (
            invites.map(invite => (
              <div key={invite.code} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-semibold text-lg text-gray-900 dark:text-white">
                        {invite.code}
                      </span>
                      {!invite.isActive && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                          Deaktiviert
                        </span>
                      )}
                      {invite.expiresAt && Date.now() > invite.expiresAt && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded">
                          Abgelaufen
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {invite.label}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                      <span>Erstellt: {formatDate(invite.createdAt)}</span>
                      {invite.expiresAt && (
                        <span>Gültig bis: {formatDate(invite.expiresAt)}</span>
                      )}
                      <span>
                        Genutzt: {invite.usageCount || 0}
                        {invite.usageLimit ? ` / ${invite.usageLimit}` : ''}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopyLink(invite.code)}
                      className={`px-3 py-1.5 text-sm rounded-lg theme-transition ${
                        copiedCode === invite.code
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {copiedCode === invite.code ? (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Kopiert
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Kopieren
                        </span>
                      )}
                    </button>
                    {invite.isActive && (
                      <button
                        onClick={() => handleDeactivateInvite(invite.code)}
                        className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Deaktivieren"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteInvite(invite.code)}
                      className="px-3 py-1.5 text-sm text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      title="Löschen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Enrolled Students Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Eingeschriebene Studierende
            {!loadingEnrollments && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                ({enrollments.length})
              </span>
            )}
          </h2>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loadingEnrollments ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Lade Teilnehmer...
            </div>
          ) : enrollments.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-2">🎒</div>
              <p className="text-gray-600 dark:text-gray-400">
                Noch keine Studierenden eingeschrieben.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                Teile einen Einladungslink, damit Studierende beitreten können.
              </p>
            </div>
          ) : (
            enrollments.map(enrollment => (
              <div key={enrollment.studentId} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-lg">
                      🎓
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {enrollment.studentName || 'Unbekannt'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {enrollment.studentEmail}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                    <p>Beigetreten: {formatDate(enrollment.enrolledAt)}</p>
                    {enrollment.inviteCode && (
                      <p className="text-xs">Code: {enrollment.inviteCode}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
